
import {useEffect} from "react";
import { ILgpHero, LgpHero } from "@sky-it-livedata/livedata-sports-design-system";
import { graphql } from "cdk/graphql";

import transformQuery from "./transformers/query-transformer";
import transformSubscription from "./transformers/subscription-transformer";
import { LgpHeroQuery, LgpHeroSubscription } from "./types";
import { log } from "../../utils/log";
import { useSubscription } from "../../hooks/useSubscription";
import { WidgetContext } from "../../types/WidgetContext";
import { lgpHeaderModalContentData, lockPageScroll, unlockPageScroll } from "../../utils/modals";
import { analyticsTrack } from "../../utils/analytics";
import { LGP_HERO_MODAL_CLOSE_EVENT, LGP_HERO_MODAL_OPEN_EVENT } from "@sky-it-livedata/livedata-sports-design-system";

export const setCustomAttributes = ({variables}: WidgetContext<LgpHeroQuery>) => {
  return {
    variables: JSON.stringify(variables),
  };
}

const subscriptionDocument = graphql("subscription UpdateLgpHeroWidget($seasonAndCompId: ID!, $gpId: ID!) {\n  onUpdateLgpHeroWidgetBySeasonAndCompIdAndGpId(\n    seasonAndCompId: $seasonAndCompId\n    gpId: $gpId\n  ) {\n    gpId\n    session {\n      label\n      name\n      status\n      podium {\n        customDriverName\n        customDriverSurname\n        customDriverShortName\n        driverImgUrl\n        flagImgUrl\n        position\n      }\n    }\n    info {\n      circuitImgUrl\n      customGpName\n      flagImgUrl\n      startDate\n      sportTag\n      endDate\n    }\n    seasonAndCompId\n  }\n}");

export function getServerProps({
  props,
}: WidgetContext<LgpHeroQuery>): ILgpHero {
  return transformQuery(props);
}

const LgpHeroWidget = ({props,
  variables,
  ...rest
}: WidgetContext<ILgpHero>) => {
  log({ props, variables, ...rest });

  const updatedProps = useSubscription<
    ILgpHero,
    LgpHeroSubscription,
    typeof subscriptionDocument
  >({
    initialData: props,
    graphQLDocument: subscriptionDocument,
    variables,
    transform: transformSubscription,
    mergeData: false,
  }) as ILgpHero;

  useEffect(() => {
    if (updatedProps.sessionStatus !== "Playing") {
      return;
    }

    const onLgpHeroModalOpen = () => {
      lockPageScroll();
      analyticsTrack('modal_open', 'modal_name', 'lgp_hero_modal' );
    };

    const onLgpHeroModalClose = () => {
      unlockPageScroll();
      analyticsTrack('modal_close', 'modal_name', 'lgp_hero_modal' );
    };

    window.addEventListener(LGP_HERO_MODAL_OPEN_EVENT, onLgpHeroModalOpen);
    window.addEventListener(LGP_HERO_MODAL_CLOSE_EVENT, onLgpHeroModalClose);
    return () => {
      window.removeEventListener(LGP_HERO_MODAL_OPEN_EVENT, onLgpHeroModalOpen);
      window.removeEventListener(LGP_HERO_MODAL_CLOSE_EVENT, onLgpHeroModalClose);
    };
  }, [updatedProps.sessionStatus]);

  return <LgpHero {...updatedProps} modalContentData={lgpHeaderModalContentData} />;
}

export default LgpHeroWidget;